import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API, formatApiErrorDetail } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  ArrowLeft,
  Plus,
  Trash,
  DotsSixVertical,
  Eye,
  Play,
  Pause,
  CaretUp,
  CaretDown
} from '@phosphor-icons/react';

const fieldTypes = [
  { value: 'text', label: 'Einzeiliger Text' },
  { value: 'textarea', label: 'Mehrzeiliger Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefon' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Zahl' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radiogruppe' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'consent', label: 'Einwilligung' },
];

const visibilityModes = [
  { value: 'always', label: 'Immer sichtbar' },
  { value: 'conditional', label: 'Bedingt sichtbar' },
  { value: 'hidden', label: 'Versteckt / vorbereitet' },
];

const defaultConsentSettings = {
  privacy: { enabled: true, required: true, version: 'v1.0', label_de: 'Ich akzeptiere die Datenschutzbestimmungen.', label_tr: 'Gizlilik şartlarını kabul ediyorum.', url: '' },
  cookies: { enabled: false, required: false, version: 'v1.0', label_de: 'Ich akzeptiere Cookies.', label_tr: 'Çerezleri kabul ediyorum.', url: '' },
  kvkk: { enabled: false, required: false, version: 'v1.0', label_de: 'Ich akzeptiere die KVKK-/DSGVO-Hinweise.', label_tr: 'KVKK/GDPR bilgilendirmesini kabul ediyorum.', url: '' },
};

const defaultLocaleSettings = {
  default_locale: 'de',
  supported_locales: ['de', 'tr'],
  translations: {
    de: { create_ticket: 'Biletimi Oluştur', success_title: 'Dein Ticket ist fertig', success_text: 'Dein persönliches Hatıra Bileti wurde erfolgreich erstellt.', share_ticket: 'Bileti teilen', reset_form: 'Yeni Bilet Oluştur' },
    tr: { create_ticket: 'Biletimi Oluştur', success_title: 'Biletin hazır', success_text: 'Kişisel Hatıra Biletin başarıyla oluşturuldu.', share_ticket: 'Bileti Paylaş', reset_form: 'Yeni Bilet Oluştur' },
  },
};

export function CampaignEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('fields');

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const [campaignResponse, templateResponse] = await Promise.all([
          axios.get(`${API}/campaigns/${id}`, { withCredentials: true }),
          axios.get(`${API}/templates`, { withCredentials: true }),
        ]);
        setCampaign({
          ...campaignResponse.data,
          consent_settings: { ...defaultConsentSettings, ...(campaignResponse.data.consent_settings || {}) },
          locale_settings: {
            ...defaultLocaleSettings,
            ...(campaignResponse.data.locale_settings || {}),
            translations: {
              ...defaultLocaleSettings.translations,
              ...(campaignResponse.data.locale_settings?.translations || {}),
            },
          },
          ticket_retention_mode: campaignResponse.data.ticket_retention_mode || 'keep_anonymized',
        });
        setTemplates(templateResponse.data || []);
      } catch (error) {
        console.error('Failed to fetch campaign:', error);
        toast.error('Campaign not found');
        navigate('/campaigns');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaign();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/campaigns/${id}`, {
        name: campaign.name,
        description: campaign.description,
        template_id: campaign.template_id,
        form_fields: campaign.form_fields,
        page_settings: campaign.page_settings,
        generate_ticket: campaign.generate_ticket,
        ticket_generation_mode: campaign.ticket_generation_mode || 'immediate',
        ticket_test_mode: campaign.ticket_test_mode || false,
        ticket_watermark_text: campaign.ticket_watermark_text || 'TEST',
        email_send_mode: campaign.email_send_mode || 'manual',
        email_test_mode: campaign.email_test_mode || 'disabled',
        consent_settings: campaign.consent_settings || defaultConsentSettings,
        locale_settings: campaign.locale_settings || defaultLocaleSettings,
        ticket_retention_mode: campaign.ticket_retention_mode || 'keep_anonymized',
        enable_qr: campaign.enable_qr,
      }, { withCredentials: true });
      toast.success('Kampagne erfolgreich gespeichert');
    } catch (error) {
      console.error('Failed to save campaign:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/publish`, {}, { withCredentials: true });
      toast.success('Kampagne erfolgreich veröffentlicht');
      setCampaign({ ...campaign, status: 'published' });
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to publish campaign');
    }
  };

  const handleUnpublish = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/unpublish`, {}, { withCredentials: true });
      toast.success('Kampagne zurück in Entwurf gesetzt');
      setCampaign({ ...campaign, status: 'draft' });
    } catch (error) {
      console.error('Failed to unpublish:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to unpublish campaign');
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      placeholder: '',
      help_text: '',
      visibility: 'always',
      required: false,
      options: [],
      order: campaign.form_fields?.length || 0,
      default_value: '',
      conditional_logic: null,
    };
    setCampaign({
      ...campaign,
      form_fields: [...(campaign.form_fields || []), newField]
    });
  };

  const updateField = (index, updates) => {
    const fields = [...campaign.form_fields];
    fields[index] = { ...fields[index], ...updates };
    setCampaign({ ...campaign, form_fields: fields });
  };

  const removeField = (index) => {
    const fields = campaign.form_fields.filter((_, i) => i !== index);
    setCampaign({ ...campaign, form_fields: fields });
  };

  const moveField = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= campaign.form_fields.length) return;

    const fields = [...campaign.form_fields];
    [fields[index], fields[targetIndex]] = [fields[targetIndex], fields[index]];
    setCampaign({
      ...campaign,
      form_fields: fields.map((field, order) => ({ ...field, order })),
    });
  };

  const updatePageSettings = (key, value) => {
    setCampaign({
      ...campaign,
      page_settings: {
        ...campaign.page_settings,
        [key]: value
      }
    });
  };

  const updateConsentSetting = (consentKey, patch) => {
    setCampaign({
      ...campaign,
      consent_settings: {
        ...campaign.consent_settings,
        [consentKey]: {
          ...(campaign.consent_settings?.[consentKey] || defaultConsentSettings[consentKey]),
          ...patch,
        },
      },
    });
  };

  const updateLocaleTranslation = (locale, key, value) => {
    setCampaign({
      ...campaign,
      locale_settings: {
        ...(campaign.locale_settings || defaultLocaleSettings),
        translations: {
          ...(campaign.locale_settings?.translations || defaultLocaleSettings.translations),
          [locale]: {
            ...((campaign.locale_settings?.translations || defaultLocaleSettings.translations)[locale] || {}),
            [key]: value,
          },
        },
      },
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
          <div className="animate-spin w-8 h-8 border-4 border-[#002FA7] border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) return null;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/campaigns')}
              className="p-2 hover:bg-zinc-100 rounded-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-zinc-900">{campaign.name}</h1>
              <p className="text-sm text-zinc-500">
                Status: <span className={`font-medium ${campaign.status === 'published' ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {campaign.status}
                </span>
                {campaign.public_url && (
                  <>
                    {' • '}
                    <a href={`/c/${campaign.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">
                      Öffentliche Seite ansehen
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {campaign.status === 'published' ? (
              <Button onClick={handleUnpublish} variant="outline">
                <Pause className="w-4 h-4 mr-2" />
                Auf Entwurf setzen
              </Button>
            ) : (
              <Button onClick={handlePublish} variant="outline">
                <Play className="w-4 h-4 mr-2" />
                Veröffentlichen
              </Button>
            )}
            <Button onClick={handleSave} className="btn-primary" disabled={saving}>
              {saving ? 'Speichert...' : 'Änderungen speichern'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="fields">Formularfelder</TabsTrigger>
            <TabsTrigger value="page">Öffentliche Seite</TabsTrigger>
            <TabsTrigger value="settings">Kampagneneinstellungen</TabsTrigger>
          </TabsList>

          {/* Form Fields Tab */}
          <TabsContent value="fields" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Formularfelder</h2>
              <Button onClick={addField} variant="outline" data-testid="add-field-btn">
                <Plus className="w-4 h-4 mr-2" />
                Feld hinzufügen
              </Button>
            </div>

            {campaign.form_fields?.length > 0 ? (
              <div className="space-y-3">
                {campaign.form_fields.map((field, index) => (
                  <div key={field.id} className="bg-white border border-zinc-200 rounded-sm p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center gap-2 text-zinc-400">
                        <DotsSixVertical className="w-5 h-5" />
                        <button
                          onClick={() => moveField(index, -1)}
                          className="p-1 rounded-sm hover:bg-zinc-100 disabled:opacity-30"
                          disabled={index === 0}
                          data-testid={`move-field-up-${field.id}`}
                        >
                          <CaretUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveField(index, 1)}
                          className="p-1 rounded-sm hover:bg-zinc-100 disabled:opacity-30"
                          disabled={index === campaign.form_fields.length - 1}
                          data-testid={`move-field-down-${field.id}`}
                        >
                          <CaretDown className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex-1 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="label-uppercase">Field Label</Label>
                            <Input
                              value={field.label}
                              onChange={(e) => updateField(index, { label: e.target.value })}
                              className="input-swiss"
                              data-testid={`field-label-${field.id}`}
                            />
                          </div>
                          <div>
                            <Label className="label-uppercase">Field Type</Label>
                            <Select
                              value={field.type}
                              onValueChange={(v) => updateField(index, { type: v })}
                            >
                              <SelectTrigger data-testid={`field-type-${field.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {fieldTypes.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="label-uppercase">Visibility</Label>
                            <Select
                              value={field.visibility || 'always'}
                              onValueChange={(v) => updateField(index, {
                                visibility: v,
                                conditional_logic: v === 'conditional'
                                  ? (field.conditional_logic || { field_id: '', operator: 'equals', value: '', action: 'show' })
                                  : null,
                              })}
                            >
                              <SelectTrigger data-testid={`field-visibility-${field.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {visibilityModes.map(mode => (
                                  <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="label-uppercase">Placeholder</Label>
                            <Input
                              value={field.placeholder || ''}
                              onChange={(e) => updateField(index, { placeholder: e.target.value })}
                              className="input-swiss"
                              placeholder="Optional..."
                              data-testid={`field-placeholder-${field.id}`}
                            />
                          </div>
                          <div>
                            <Label className="label-uppercase">Help Text</Label>
                            <Input
                              value={field.help_text || ''}
                              onChange={(e) => updateField(index, { help_text: e.target.value })}
                              className="input-swiss"
                              placeholder="Additional guidance"
                              data-testid={`field-helptext-${field.id}`}
                            />
                          </div>
                          <div>
                            <Label className="label-uppercase">Default Value</Label>
                            <Input
                              value={field.default_value || ''}
                              onChange={(e) => updateField(index, { default_value: e.target.value })}
                              className="input-swiss"
                              placeholder="Optional default"
                              data-testid={`field-default-${field.id}`}
                            />
                          </div>
                        </div>

                        {field.visibility === 'conditional' && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border border-zinc-200 rounded-sm p-4 bg-zinc-50">
                            <div>
                              <Label className="label-uppercase">Depends On</Label>
                              <Select
                                value={field.conditional_logic?.field_id || 'none'}
                                onValueChange={(v) => updateField(index, {
                                  conditional_logic: {
                                    field_id: v === 'none' ? '' : v,
                                    operator: field.conditional_logic?.operator || 'equals',
                                    value: field.conditional_logic?.value || '',
                                    action: 'show',
                                  },
                                })}
                              >
                                <SelectTrigger data-testid={`field-condition-source-${field.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select field</SelectItem>
                                  {campaign.form_fields.filter((candidate) => candidate.id !== field.id).map((candidate) => (
                                    <SelectItem key={candidate.id} value={candidate.id}>{candidate.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="label-uppercase">Operator</Label>
                              <Select
                                value={field.conditional_logic?.operator || 'equals'}
                                onValueChange={(v) => updateField(index, {
                                  conditional_logic: {
                                    field_id: field.conditional_logic?.field_id || '',
                                    operator: v,
                                    value: field.conditional_logic?.value || '',
                                    action: 'show',
                                  },
                                })}
                              >
                                <SelectTrigger data-testid={`field-condition-operator-${field.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="equals">Equals</SelectItem>
                                  <SelectItem value="not_equals">Not equals</SelectItem>
                                  <SelectItem value="contains">Contains</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="label-uppercase">Compare Value</Label>
                              <Input
                                value={field.conditional_logic?.value || ''}
                                onChange={(e) => updateField(index, {
                                  conditional_logic: {
                                    field_id: field.conditional_logic?.field_id || '',
                                    operator: field.conditional_logic?.operator || 'equals',
                                    value: e.target.value,
                                    action: 'show',
                                  },
                                })}
                                className="input-swiss"
                                data-testid={`field-condition-value-${field.id}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                            className="rounded border-zinc-300"
                            data-testid={`field-required-${field.id}`}
                          />
                          Required
                        </label>
                        <button
                          onClick={() => removeField(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-sm"
                          data-testid={`field-remove-${field.id}`}
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm">
                <p className="text-zinc-500 mb-4">Noch keine Formularfelder vorhanden.</p>
                <Button onClick={addField} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Feld hinzufügen
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Page Settings Tab */}
          <TabsContent value="page" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
              <h2 className="text-lg font-bold">Öffentliche Seiteninhalte</h2>
              
              <div>
                <Label className="label-uppercase">Headline</Label>
                <Input
                  value={campaign.page_settings?.headline || ''}
                  onChange={(e) => updatePageSettings('headline', e.target.value)}
                  className="input-swiss"
                  placeholder="Willkommen zu unserem Event..."
                />
              </div>

              <div>
                <Label className="label-uppercase">Description</Label>
                <Textarea
                  value={campaign.page_settings?.description || ''}
                  onChange={(e) => updatePageSettings('description', e.target.value)}
                  className="input-swiss"
                  rows={3}
                  placeholder="Kurze Beschreibung..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-uppercase">Button-Text</Label>
                  <Input
                    value={campaign.page_settings?.button_text || 'Absenden'}
                    onChange={(e) => updatePageSettings('button_text', e.target.value)}
                    className="input-swiss"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Erfolgsmeldung</Label>
                  <Input
                    value={campaign.page_settings?.success_message || ''}
                    onChange={(e) => updatePageSettings('success_message', e.target.value)}
                    className="input-swiss"
                    placeholder="Vielen Dank für deine Einreichung!"
                  />
                </div>
              </div>

              <div>
                  <Label className="label-uppercase">Datenschutztext</Label>
                <Textarea
                  value={campaign.page_settings?.privacy_text || ''}
                  onChange={(e) => updatePageSettings('privacy_text', e.target.value)}
                  className="input-swiss"
                  rows={2}
                  placeholder="Datenschutzhinweis..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-uppercase">Primärfarbe</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={campaign.page_settings?.primary_color || '#002FA7'}
                      onChange={(e) => updatePageSettings('primary_color', e.target.value)}
                      className="w-10 h-10 rounded-sm cursor-pointer"
                    />
                    <Input
                      value={campaign.page_settings?.primary_color || '#002FA7'}
                      onChange={(e) => updatePageSettings('primary_color', e.target.value)}
                      className="input-swiss flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase">Hintergrundfarbe</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={campaign.page_settings?.background_color || '#FFFFFF'}
                      onChange={(e) => updatePageSettings('background_color', e.target.value)}
                      className="w-10 h-10 rounded-sm cursor-pointer"
                    />
                    <Input
                      value={campaign.page_settings?.background_color || '#FFFFFF'}
                      onChange={(e) => updatePageSettings('background_color', e.target.value)}
                      className="input-swiss flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Campaign Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
              <h2 className="text-lg font-bold">Kampagnendetails</h2>
              
              <div>
                <Label className="label-uppercase">Kampagnenname</Label>
                <Input
                  value={campaign.name || ''}
                  onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                  className="input-swiss"
                />
              </div>

              <div>
                <Label className="label-uppercase">URL-Slug</Label>
                <Input
                  value={campaign.slug || ''}
                  disabled
                  className="input-swiss bg-zinc-50"
                />
                <p className="text-xs text-zinc-500 mt-1">Der URL-Slug kann nach der Erstellung nicht geändert werden.</p>
              </div>

              <div>
                <Label className="label-uppercase">Beschreibung</Label>
                <Textarea
                  value={campaign.description || ''}
                  onChange={(e) => setCampaign({ ...campaign, description: e.target.value })}
                  className="input-swiss"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-uppercase">Abteilung</Label>
                  <Input value={campaign.department_name || ''} disabled className="input-swiss bg-zinc-50" />
                </div>
                <div>
                  <Label className="label-uppercase">Domain</Label>
                  <Input value={campaign.domain_name || ''} disabled className="input-swiss bg-zinc-50" />
                </div>
              </div>

              <div className="pt-4 border-t border-zinc-200 space-y-3">
                <h3 className="font-semibold">Optionen</h3>
                <div>
                  <Label className="label-uppercase">Ticket Template</Label>
                  <Select
                    value={campaign.template_id || 'none'}
                    onValueChange={(value) => setCampaign({ ...campaign, template_id: value === 'none' ? null : value })}
                  >
                    <SelectTrigger data-testid="campaign-template-select">
                      <SelectValue placeholder="Template auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Template</SelectItem>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(campaign.template_id ? `/templates/${campaign.template_id}/edit?campaign_id=${id}` : `/templates/new?campaign_id=${id}`)}
                    data-testid="campaign-open-template-designer-button"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Template im Kampagnenkontext öffnen
                  </Button>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={campaign.generate_ticket !== false}
                    onChange={(e) => setCampaign({ ...campaign, generate_ticket: e.target.checked })}
                    className="rounded border-zinc-300"
                  />
                  <span>Hatıra Bileti erzeugen</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={campaign.enable_qr !== false}
                    onChange={(e) => setCampaign({ ...campaign, enable_qr: e.target.checked })}
                    className="rounded border-zinc-300"
                  />
                  <span>QR-Code auf dem Ticket anzeigen</span>
                </label>
                <div>
                  <Label className="label-uppercase">Ticket Generation Mode</Label>
                  <Select
                    value={campaign.ticket_generation_mode || 'immediate'}
                    onValueChange={(value) => setCampaign({ ...campaign, ticket_generation_mode: value })}
                  >
                    <SelectTrigger data-testid="campaign-ticket-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Sofort nach Submission</SelectItem>
                      <SelectItem value="after_approval">Erst nach Freigabe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={campaign.ticket_test_mode || false}
                    onChange={(e) => setCampaign({ ...campaign, ticket_test_mode: e.target.checked })}
                    className="rounded border-zinc-300"
                    data-testid="campaign-ticket-testmode-checkbox"
                  />
                  <span>Testmodus aktivieren (mit Wasserzeichen)</span>
                </label>
                <div>
                  <Label className="label-uppercase">Test-Wasserzeichen</Label>
                  <Input
                    value={campaign.ticket_watermark_text || 'TEST'}
                    onChange={(e) => setCampaign({ ...campaign, ticket_watermark_text: e.target.value })}
                    className="input-swiss"
                    data-testid="campaign-ticket-watermark-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">E-Mail Versandmodus</Label>
                  <Select
                    value={campaign.email_send_mode || 'manual'}
                    onValueChange={(value) => setCampaign({ ...campaign, email_send_mode: value })}
                  >
                    <SelectTrigger data-testid="campaign-email-sendmode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Sofort nach Ticket-Erzeugung</SelectItem>
                      <SelectItem value="after_approval">Erst nach Freigabe</SelectItem>
                      <SelectItem value="manual">Nur manuell durch Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">E-Mail Testmodus</Label>
                  <Select
                    value={campaign.email_test_mode || 'disabled'}
                    onValueChange={(value) => setCampaign({ ...campaign, email_test_mode: value })}
                  >
                    <SelectTrigger data-testid="campaign-email-testmode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disabled">Normaler Versand</SelectItem>
                      <SelectItem value="redirect_to_admin">Im Test an Admin umleiten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">Ticket-Verhalten bei Anonymisierung</Label>
                  <Select value={campaign.ticket_retention_mode || 'keep_anonymized'} onValueChange={(value) => setCampaign({ ...campaign, ticket_retention_mode: value })}>
                    <SelectTrigger data-testid="campaign-ticket-retention-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep_anonymized">Ticket behalten und anonymisiert neu rendern</SelectItem>
                      <SelectItem value="delete_with_submission">Ticket mit der Submission archivieren</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-4 border-t border-zinc-200 space-y-4">
                  <h3 className="font-semibold">Datenschutz &amp; Einwilligungen</h3>
                  {['privacy', 'cookies', 'kvkk'].map((consentKey) => (
                    <div key={consentKey} className="border border-zinc-200 rounded-sm p-4 space-y-3 bg-zinc-50">
                      <div className="flex items-center justify-between gap-4">
                        <p className="font-medium text-zinc-900 capitalize">{consentKey}</p>
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={campaign.consent_settings?.[consentKey]?.enabled || false} onChange={(e) => updateConsentSetting(consentKey, { enabled: e.target.checked })} data-testid={`campaign-consent-enabled-${consentKey}`} />
                          Aktiv
                        </label>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={campaign.consent_settings?.[consentKey]?.required || false} onChange={(e) => updateConsentSetting(consentKey, { required: e.target.checked })} data-testid={`campaign-consent-required-${consentKey}`} />
                        Pflichtfeld
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input value={campaign.consent_settings?.[consentKey]?.label_de || ''} onChange={(e) => updateConsentSetting(consentKey, { label_de: e.target.value })} placeholder="Text DE" data-testid={`campaign-consent-label-de-${consentKey}`} />
                        <Input value={campaign.consent_settings?.[consentKey]?.label_tr || ''} onChange={(e) => updateConsentSetting(consentKey, { label_tr: e.target.value })} placeholder="Text TR" data-testid={`campaign-consent-label-tr-${consentKey}`} />
                        <Input value={campaign.consent_settings?.[consentKey]?.url || ''} onChange={(e) => updateConsentSetting(consentKey, { url: e.target.value })} placeholder="URL Override" data-testid={`campaign-consent-url-${consentKey}`} />
                        <Input value={campaign.consent_settings?.[consentKey]?.version || 'v1.0'} onChange={(e) => updateConsentSetting(consentKey, { version: e.target.value })} placeholder="Version" data-testid={`campaign-consent-version-${consentKey}`} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-zinc-200 space-y-4">
                  <h3 className="font-semibold">Öffentliche Texte (DE / TR)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-zinc-700">Deutsch</h4>
                      <Input value={campaign.locale_settings?.translations?.de?.create_ticket || ''} onChange={(e) => updateLocaleTranslation('de', 'create_ticket', e.target.value)} placeholder="Button 'Biletimi Oluştur'" data-testid="campaign-locale-de-create-ticket" />
                      <Input value={campaign.locale_settings?.translations?.de?.success_title || ''} onChange={(e) => updateLocaleTranslation('de', 'success_title', e.target.value)} placeholder="Erfolgstitel" data-testid="campaign-locale-de-success-title" />
                      <Input value={campaign.locale_settings?.translations?.de?.download_ticket || ''} onChange={(e) => updateLocaleTranslation('de', 'download_ticket', e.target.value)} placeholder="Download-Button" data-testid="campaign-locale-de-download" />
                      <Input value={campaign.locale_settings?.translations?.de?.share_ticket || ''} onChange={(e) => updateLocaleTranslation('de', 'share_ticket', e.target.value)} placeholder="Share-Button" data-testid="campaign-locale-de-share" />
                      <Input value={campaign.locale_settings?.translations?.de?.reset_form || ''} onChange={(e) => updateLocaleTranslation('de', 'reset_form', e.target.value)} placeholder="Reset-Button" data-testid="campaign-locale-de-reset" />
                    </div>
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-zinc-700">Türkçe</h4>
                      <Input value={campaign.locale_settings?.translations?.tr?.create_ticket || ''} onChange={(e) => updateLocaleTranslation('tr', 'create_ticket', e.target.value)} placeholder="'Biletimi Oluştur'" data-testid="campaign-locale-tr-create-ticket" />
                      <Input value={campaign.locale_settings?.translations?.tr?.success_title || ''} onChange={(e) => updateLocaleTranslation('tr', 'success_title', e.target.value)} placeholder="Başlık" data-testid="campaign-locale-tr-success-title" />
                      <Input value={campaign.locale_settings?.translations?.tr?.download_ticket || ''} onChange={(e) => updateLocaleTranslation('tr', 'download_ticket', e.target.value)} placeholder="İndir butonu" data-testid="campaign-locale-tr-download" />
                      <Input value={campaign.locale_settings?.translations?.tr?.share_ticket || ''} onChange={(e) => updateLocaleTranslation('tr', 'share_ticket', e.target.value)} placeholder="Paylaş butonu" data-testid="campaign-locale-tr-share" />
                      <Input value={campaign.locale_settings?.translations?.tr?.reset_form || ''} onChange={(e) => updateLocaleTranslation('tr', 'reset_form', e.target.value)} placeholder="Yeni bilet butonu" data-testid="campaign-locale-tr-reset" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
